import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'
import deleteBinIcon from '@/assets/delete-bin-line 1.svg'
import {
  ConfirmationModalShell,
} from '@/shared/components/confirmation/ConfirmationModalShell'
import {
  confirmationDangerButtonSx,
  confirmationHeadlineSx,
  confirmationSecondaryButtonSx,
} from '@/shared/components/confirmation/confirmationModalStyles'

type Props = Readonly<{
  open: boolean
  title: ReactNode
  onCancel: () => void
  onConfirm: () => void
  isPending?: boolean
  dialogTestId: string
  cancelTestId: string
  confirmTestId: string
  confirmLabel?: string
}>

/** Figma 6074:13956 — Library delete/remove confirmation modal. */
export function LibraryDeleteConfirmDialog({
  open,
  title,
  onCancel,
  onConfirm,
  isPending = false,
  dialogTestId,
  cancelTestId,
  confirmTestId,
  confirmLabel = 'Yes Remove',
}: Props) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={isPending ? undefined : onCancel}
      disableEscapeKeyDown={isPending}
      dialogTestId={dialogTestId}
      ariaLabelledBy={`${dialogTestId}-title`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            height: '112px',
            pt: '32px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: '#fff0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={deleteBinIcon}
              alt=""
              aria-hidden
              data-testid={`${dialogTestId}-icon`}
              sx={{ width: 24, height: 24, display: 'block' }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            minHeight: '67px',
            px: '24px',
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            id={`${dialogTestId}-title`}
            component="h2"
            sx={{
              ...confirmationHeadlineSx,
              maxWidth: '353px',
              m: 0,
            }}
          >
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            height: '84px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isPending}
            data-testid={cancelTestId}
            sx={{
              ...confirmationSecondaryButtonSx,
              minWidth: 96,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isPending}
            data-testid={confirmTestId}
            sx={{
              ...confirmationDangerButtonSx,
              minWidth: 131,
            }}
          >
            {confirmLabel}
          </Button>
        </Box>
      </Box>
    </ConfirmationModalShell>
  )
}
